import { onMounted } from 'vue';
import AOS from 'aos';
import 'aos/dist/aos.css';

const defaultOptions = {
  duration: 800,
  easing: 'ease-out',
  once: false,
  mirror: false,
  anchorPlacement: 'top-bottom',
  disable: false,
};

// Global state - shared across all component instances
let resizeTimeoutId;
let recalculateTimeoutId;
let isInitialized = false;
let previousWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
let currentOptions = {};
let mountCount = 0;
let isReinitializing = false;

// Completely reinitialize AOS from scratch
const destroyAndReinitAos = () => {
  // Prevent multiple simultaneous reinitializations
  if (isReinitializing) return;
  isReinitializing = true;

  const aosElements = document.querySelectorAll('[data-aos]');

  aosElements.forEach(el => {
    el.classList.remove('aos-animate', 'aos-init');
    el.style.removeProperty('transform');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transition-delay');
    el.style.removeProperty('transition-duration');
    el.style.removeProperty('transition-timing-function');
    el.style.visibility = '';
    el.style.opacity = '';
  });

  void document.body.offsetHeight;
  void document.documentElement.offsetHeight;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Fully reinitialize AOS - this recalculates all positions from scratch
      AOS.init(currentOptions);
      AOS.refreshHard();

      // Small delay then trigger animations
      setTimeout(() => {
        AOS.refresh();
        window.dispatchEvent(new Event('scroll'));

        // Mark reinitialization as complete
        setTimeout(() => {
          isReinitializing = false;
        }, 100);
      }, 50);
    });
  });
};

const handleResize = () => {
  // Ignore resize events during reinitialization
  if (isReinitializing) return;

  const currentWidth = window.innerWidth;

  // Detect if we crossed any responsive breakpoint
  const crossedBreakpoint =
    (previousWidth <= 480 && currentWidth > 480) ||
    (previousWidth > 480 && currentWidth <= 480) ||
    (previousWidth <= 768 && currentWidth > 768) ||
    (previousWidth > 768 && currentWidth <= 768) ||
    (previousWidth <= 1024 && currentWidth > 1024) ||
    (previousWidth > 1024 && currentWidth <= 1024);

  // Clear any pending timeouts
  if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
  if (recalculateTimeoutId) clearTimeout(recalculateTimeoutId);

  // Update previous width
  previousWidth = currentWidth;

  if (crossedBreakpoint) {
    // When crossing breakpoints, wait for CSS media queries to fully apply
    resizeTimeoutId = setTimeout(() => {
      destroyAndReinitAos();

      recalculateTimeoutId = setTimeout(() => {
        if (!isReinitializing) {
          AOS.refreshHard();
          window.dispatchEvent(new Event('scroll'));
        }
      }, 400);
    }, 250);
  } else {
    // Minor resize within same breakpoint - just refresh
    resizeTimeoutId = setTimeout(() => {
      AOS.refresh();
    }, 100);
  }
};

export const useAos = (options = {}) => {
  currentOptions = { ...defaultOptions, ...options };

  onMounted(() => {
    mountCount++;
    if (!isInitialized) {
      AOS.init(currentOptions);
      window.addEventListener('resize', handleResize, { passive: true });
      window.addEventListener(
        'orientationchange',
        () => {
          setTimeout(destroyAndReinitAos, 400);
        },
        { passive: true }
      );
      isInitialized = true;
      previousWidth = window.innerWidth;
    }
  });
};
